import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Radar, Wrench, Zap, Hammer, Truck, Wind, Bug, Home, HeartPulse, Building2, ExternalLink, Play } from "lucide-react";

const DEMOS = [
  { key: "landlord",     slug: "landlord",    name: "Kensington Property Group", Icon: Building2,  color: "#0f172a", hook: "Full PM stack · AppFolio parity · Ask Radar · Safety Board" },
  { key: "landscaping",  slug: "landscaping", name: "GreenSlate Landscaping & Snow", Icon: Radar, color: "#15803d", hook: "Route Radar · snow accounts · low-salt alert · draft HOA invoice" },
  { key: "plumbing",     slug: "plumbing",    name: "PipeDream Plumbing", Icon: Wrench,    color: "#0369a1", hook: "AI Leak Diagnose — photo → diagnosis + parts + quote" },
  { key: "electrical",   slug: "electrical",  name: "Voltaic Electric", Icon: Zap,           color: "#ca8a04", hook: "AI load-calc · panel schedule builder · NEC-flagged findings" },
  { key: "handyman",     slug: "handyman",    name: "One-Truck Handyman", Icon: Hammer,      color: "#b45309", hook: "Booking Radar · tap-to-pay · AI estimate drafter" },
  { key: "vendors",      slug: "vendors",     name: "Reliant Maintenance Vendor", Icon: Truck, color: "#4338ca", hook: "Bridge Inbox — WOs from Buildium / AppFolio / Yardi · SLA scorecard" },
  { key: "hvac",         slug: "hvac",        name: "Coolflow HVAC", Icon: Wind,             color: "#0e7490", hook: "Equipment Radar · warranty tracking · filter schedules" },
  { key: "pest_control", slug: "pest",        name: "Buzzkill Pest Control", Icon: Bug,      color: "#7c2d12", hook: "Recurring accounts · '4 stops today' · one overdue" },
  { key: "roofing",      slug: "roofing",     name: "Ironclad Roofing", Icon: Home,          color: "#374151", hook: "$71,200 pipeline · storm-damage intake" },
  { key: "home_health",  slug: "home-care",   name: "Sunrise Home Care", Icon: HeartPulse,   color: "#be123c", hook: "Client Care Radar · visit logs · care flags" },
];

const PUBLIC_TOOLS = [
  { label: "Try Fingerprint Demo (landing page)", href: "/#fingerprint", note: "Upload any house photo → AI extracts street #, style, unit count" },
  { label: "Landing page — Numbers That Roll + Crew Storyboard", href: "/", note: "Public marketing sections" },
];

export default function DemosHub() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const [launched, setLaunched] = useState(false);
  const allowed = user && (user.platform_role === "super_admin" || user.is_founder);
  if (!allowed && !launched) return <Navigate to="/admin" replace />;

  const launch = async (d) => {
    setBusy(d.key);
    try {
      const { data } = await api.post(`/public/demo/login/${d.key}`);
      setLaunched(true);
      localStorage.setItem("access_token", data.access_token);
      if (setUser && data.user) setUser(data.user);
      window.dispatchEvent(new Event("auth-changed"));
      toast.success(`${d.name} loaded — read-only playground, resets every 30 min`);
      nav(data.landing || "/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not launch demo"));
    } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white p-6 md:p-10" data-testid="demos-hub">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-400">Founder only · hidden page</div>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-1">Demo Launchpad</h1>
            <p className="text-slate-400 text-sm mt-2 max-w-xl">
              One tap drops you into a seeded, read-only demo workspace. <b className="text-amber-300">Heads up:</b> launching a demo signs you out of PMR in this browser — sign back in when you're done.
            </p>
          </div>
          <button onClick={() => nav("/admin")} data-testid="demos-back-btn"
            className="px-4 py-2 border-2 border-slate-600 text-slate-300 font-black text-xs uppercase tracking-widest hover:border-white hover:text-white transition-colors">
            ← Back to PMR
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DEMOS.map((d) => (
            <div key={d.key} data-testid={`demo-card-${d.key}`}
              className="group bg-slate-900/70 border border-slate-700 hover:border-cyan-400 transition-colors p-4 flex gap-4">
              <div className="w-12 h-12 shrink-0 flex items-center justify-center" style={{ background: d.color }}>
                <d.Icon size={22} strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-black text-base leading-tight">{d.name}</div>
                <div className="text-[11px] text-slate-400 mt-1">{d.hook}</div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => launch(d)} disabled={!!busy} data-testid={`demo-launch-${d.key}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-400 text-[#0f172a] font-black text-[11px] uppercase tracking-widest hover:bg-cyan-300 disabled:opacity-50 transition-colors">
                    <Play size={12} strokeWidth={3}/> {busy === d.key ? "Loading…" : "Launch demo"}
                  </button>
                  <a href={`/${d.slug}`} target="_blank" rel="noreferrer" data-testid={`demo-storefront-${d.key}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-600 text-slate-300 font-black text-[11px] uppercase tracking-widest hover:border-white hover:text-white transition-colors">
                    <ExternalLink size={12}/> Storefront /{d.slug}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 mb-3">Public no-login tools</div>
          <div className="space-y-2">
            {PUBLIC_TOOLS.map((t) => (
              <a key={t.href} href={t.href} target="_blank" rel="noreferrer"
                className="block bg-slate-900/50 border border-slate-800 hover:border-slate-500 p-3 transition-colors">
                <div className="font-bold text-sm">{t.label} <ExternalLink size={12} className="inline ml-1 opacity-60"/></div>
                <div className="text-[11px] text-slate-500">{t.note}</div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
